/**
 * Admin-authored page content, rendered without giving it the customer's session.
 *
 * **The sandbox is the design.** `GET /cms-pages/{slug}` answers `content` that may be HTML, written by
 * whoever administers the restaurant, and it is rendered in an iframe with
 *
 * ```
 * sandbox="allow-scripts allow-popups allow-forms allow-modals"
 * ```
 *
 * — with `allow-same-origin` **absent**. Scripts run, but at a null origin, so they cannot reach the
 * parent's cookies, `localStorage` or the customer's token. The obvious simplification during a port is
 * `dangerouslySetInnerHTML`, and it would turn stored admin HTML into stored XSS with full access to a
 * signed-in customer's session. It is not simplified.
 *
 * **The height handshake is equally deliberate.** The frame posts its height; the parent accepts the
 * message only when `event.source` is that frame's `contentWindow`. Comparing `event.origin` would be
 * the natural-looking check and would prove nothing — a null-origin frame's origin is the *string*
 * `"null"`, which anything can claim. And the height is clamped, so a hostile or broken page cannot grow
 * the document without bound.
 *
 * **One change from legacy**: the wrapper's colours are passed in rather than hard-coded to `#1f2937`
 * and a `body.dark` variant. The frame cannot see the parent's CSS variables, so a franchise's theme
 * reaches its own content only if it is resolved outside and handed in — §9.3.
 */

/** Legacy's own test: an opening tag. Plain text skips the frame entirely. */
export function looksLikeHtml(content: string): boolean {
  return /<\s*[a-z!][^>]*>/i.test(content)
}

export const MIN_FRAME_HEIGHT = 100
export const MAX_FRAME_HEIGHT = 20_000
/** Legacy's own slack, so a frame does not sit one pixel short and scroll. */
const HEIGHT_SLACK = 8

export const FRAME_SANDBOX = 'allow-scripts allow-popups allow-forms allow-modals'

export interface FrameColours {
  /** Body text, resolved from the theme by the caller. */
  text: string
  link: string
}

/**
 * The height to apply for a posted message, or `null` when the message is not one to act on.
 *
 * `source` is compared by identity against the frame's own `contentWindow`. That is the whole check —
 * an origin string from a sandboxed frame carries no information.
 */
export function heightFromMessage(
  event: { data: unknown; source: unknown },
  frameWindow: unknown,
): number | null {
  if (frameWindow === null || frameWindow === undefined) return null
  if (event.source !== frameWindow) return null
  const data = event.data
  if (typeof data !== 'object' || data === null) return null
  const message = data as { type?: unknown; h?: unknown }
  if (message.type !== 'cms-page-height') return null
  if (typeof message.h !== 'number' || !Number.isFinite(message.h)) return null
  return Math.max(MIN_FRAME_HEIGHT, Math.min(MAX_FRAME_HEIGHT, Math.ceil(message.h) + HEIGHT_SLACK))
}

/** Nothing is escaped: the content is trusted to be HTML and untrusted to be safe, hence the sandbox. */
export function buildFrameDocument(content: string, colours: FrameColours): string {
  return `<!doctype html>
<html lang="sk">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  html, body { margin: 0; padding: 0; background: transparent; }
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; font-size: 15px; line-height: 1.65; color: ${colours.text}; }
  a { color: ${colours.link}; text-decoration: underline; }
  img, video, iframe { max-width: 100%; height: auto; }
  p { margin: 0 0 0.85em; }
  h1, h2, h3, h4 { margin: 1.2em 0 0.6em; line-height: 1.25; }
</style>
</head>
<body>
${content}
<script>
  (function () {
    function post() {
      try {
        var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        parent.postMessage({ type: "cms-page-height", h: h }, "*");
      } catch (e) {}
    }
    if (document.readyState === "complete") post();
    else window.addEventListener("load", post);
    if (typeof ResizeObserver !== "undefined") {
      try { new ResizeObserver(post).observe(document.body); } catch (e) {}
    }
    setTimeout(post, 100);
    setTimeout(post, 500);
    setTimeout(post, 1500);
  })();
</script>
</body>
</html>`
}
